import type { Transaction, TransactionRevertReason } from 'types/api/transaction';

import hexToUtf8 from 'lib/hexToUtf8';
import { HEX_REGEXP } from 'lib/regexp';

const RESULT_LABELS: Record<string, string> = {
  awaiting_internal_transactions: 'Execution reverted',
  execution_reverted: 'Execution reverted',
  'execution reverted': 'Execution reverted',
  error: 'Execution reverted',
  failed: 'Transaction failed',
  out_of_gas: 'Out of gas',
  'out of gas': 'Out of gas',
};

function formatRevertReason(reason: TransactionRevertReason): string | undefined {
  if ('raw' in reason) {
    if (!reason.raw) {
      return undefined;
    }
    if (HEX_REGEXP.test(reason.raw)) {
      const decoded = hexToUtf8(reason.raw).replace(/\0/g, '').trim();
      if (decoded) {
        return decoded;
      }
    }
    return reason.raw;
  }

  if (reason.method_call) {
    const params = reason.parameters
      ?.map((param) => {
        const value = Array.isArray(param.value) ? param.value.join(', ') : String(param.value ?? '');
        return param.name ? `${ param.name }=${ value }` : value;
      })
      .filter(Boolean)
      .join(', ');
    return params ? `${ reason.method_call }: ${ params }` : reason.method_call;
  }

  return undefined;
}

function humanizeResult(result: string): string {
  const normalized = result.trim().toLowerCase();
  if (RESULT_LABELS[normalized]) {
    return RESULT_LABELS[normalized];
  }
  return result
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Human-readable failure reason for list/detail tooltips.
 * Prefers decoded revert_reason; falls back to a cleaned `result` string.
 */
export default function getTxErrorText(
  tx: Pick<Transaction, 'status' | 'result' | 'revert_reason'>,
): string | undefined {
  if (tx.status !== 'error') {
    return undefined;
  }

  if (tx.revert_reason) {
    const fromRevert = formatRevertReason(tx.revert_reason);
    if (fromRevert) {
      return fromRevert;
    }
  }

  if (tx.result && tx.result.toLowerCase() !== 'success') {
    return humanizeResult(tx.result);
  }

  return 'Execution reverted';
}
